/**
 * Sound Effect Hooks for LoLo Solitaire
 * Defines all sound effect scenarios and their triggers
 */

import {
    playSound,
    playRandomSound,
    getPitchForCardCount,
    getPitchForCardRank,
    getPitchForFoundationSize,
} from "./soundManager";

/**
 * Sound library containers for each effect type
 * These will be populated with actual audio files
 */
export interface SoundLibraries {
    undo?: { [key: string]: { mp3?: string; webm?: string } };
    pickupCard?: { [key: string]: { mp3?: string; webm?: string } };
    tapCard?: { [key: string]: { mp3?: string; webm?: string } };
    cardFlipped?: { [key: string]: { mp3?: string; webm?: string } };
    cardMoved?: { [key: string]: { mp3?: string; webm?: string } };
    cardToFoundation?: { [key: string]: { mp3?: string; webm?: string } };
    stockClicked?: { [key: string]: { mp3?: string; webm?: string } };
    shuffle?: { [key: string]: { mp3?: string; webm?: string } };
    pileFlipped?: { [key: string]: { mp3?: string; webm?: string } };
    invalidDrop?: { [key: string]: { mp3?: string; webm?: string } };
    invalidTap?: { [key: string]: { mp3?: string; webm?: string } };
    dealing?: { [key: string]: { mp3?: string; webm?: string } };
    soundEnabled?: { [key: string]: { mp3?: string; webm?: string } };
}

export class SoundHooks {
    private soundLibraries: SoundLibraries = {};

    constructor(libraries: SoundLibraries) {
        this.soundLibraries = libraries;
    }

    /**
     * Play undo sound effect
     */
    async undo(): Promise<void> {
        if (this.soundLibraries.undo) {
            await playRandomSound(this.soundLibraries.undo, 1.0, 0.8);
        }
    }

    /**
     * Play sound when picking up card(s) for drag
     * @param cardCount Number of cards being picked up
     * @param usePitchShift If true, pitch shifts based on card count
     */
    async pickupCard(
        cardCount: number = 1,
        usePitchShift: boolean = false,
    ): Promise<void> {
        if (this.soundLibraries.pickupCard) {
            const pitch = usePitchShift ? getPitchForCardCount(cardCount) : 1.0;
            await playRandomSound(this.soundLibraries.pickupCard, pitch, 0.7);
        }
    }

    /**
     * Play sound when tapping a card
     * @param cardRank Rank of the card (1-13)
     * @param usePitchShift If true, pitch shifts based on card rank
     */
    async tapCard(
        cardRank: number = 1,
        usePitchShift: boolean = false,
    ): Promise<void> {
        if (this.soundLibraries.tapCard) {
            const pitch = usePitchShift ? getPitchForCardRank(cardRank) : 1.0;
            await playRandomSound(this.soundLibraries.tapCard, pitch, 0.7);
        }
    }

    /**
     * Play sound when a card is flipped over
     */
    async cardFlipped(): Promise<void> {
        if (this.soundLibraries.cardFlipped) {
            await playRandomSound(this.soundLibraries.cardFlipped, 1.0, 0.7);
        }
    }

    /**
     * Play sound when a card moves (but not to foundation)
     */
    async cardMoved(): Promise<void> {
        if (this.soundLibraries.cardMoved) {
            await playRandomSound(this.soundLibraries.cardMoved, 1.0, 0.7);
        }
    }

    /**
     * Play sound when a card moves onto the foundation
     * @param foundationPileSize Total number of cards in the foundation pile after the move
     * @param usePitchShift If true, pitch shifts based on foundation size
     */
    async cardToFoundation(
        foundationPileSize: number = 1,
        usePitchShift: boolean = false,
    ): Promise<void> {
        if (this.soundLibraries.cardToFoundation) {
            const pitch = usePitchShift
                ? getPitchForFoundationSize(foundationPileSize)
                : 1.0;
            await playRandomSound(this.soundLibraries.cardToFoundation, pitch, 0.8);
        }
    }

    /**
     * Play sound when the stock pile is clicked
     */
    async stockClicked(): Promise<void> {
        if (this.soundLibraries.stockClicked) {
            await playRandomSound(this.soundLibraries.stockClicked, 1.0, 0.8);
        }
    }

    /**
     * Play sound when a pile is shuffled
     */
    async shuffle(): Promise<void> {
        if (this.soundLibraries.shuffle) {
            await playRandomSound(this.soundLibraries.shuffle, 1.0, 0.8);
        }
    }

    /**
     * Play sound when a pile is flipped over
     */
    async pileFlipped(): Promise<void> {
        if (this.soundLibraries.pileFlipped) {
            await playRandomSound(this.soundLibraries.pileFlipped, 1.0, 0.8);
        }
    }

    /**
     * Play sound when a card is dropped in an invalid position
     */
    async invalidDrop(): Promise<void> {
        if (this.soundLibraries.invalidDrop) {
            await playRandomSound(this.soundLibraries.invalidDrop, 1.0, 0.7);
        }
    }

    /**
     * Play sound when a card is tapped without a valid move
     */
    async invalidTap(): Promise<void> {
        if (this.soundLibraries.invalidTap) {
            await playRandomSound(this.soundLibraries.invalidTap, 1.0, 0.7);
        }
    }

    /**
     * Play sound when the game is dealing cards
     */
    async dealing(): Promise<void> {
        if (this.soundLibraries.dealing) {
            await playRandomSound(this.soundLibraries.dealing, 1.0, 0.8);
        }
    }

    /**
     * Play sound when sound is enabled
     */
    async soundEnabled(): Promise<void> {
        if (this.soundLibraries.soundEnabled) {
            await playRandomSound(this.soundLibraries.soundEnabled, 1.0, 0.8);
        }
    }
}
